import { isValidElement, useMemo } from 'react';
import { isPlainObject } from '../../_util/is';
import { devUseWarning } from '../../_util/warning';
function normalizeMask(mask) {
    if (isValidElement(mask)) {
        return [mask, undefined];
    }
    if (typeof mask === 'boolean' || isPlainObject(mask)) {
        return [undefined, mask];
    }
    return [undefined, undefined];
}
export default function usePreviewConfig(preview) {
    // Get origin preview config
    const rawPreviewConfig = useMemo(() => {
        if (typeof preview === 'boolean') {
            return preview ? {} : null;
        }
        return isPlainObject(preview) ? preview : {};
    }, [preview]);
    const splittedPreviewConfig = useMemo(() => {
        if (!rawPreviewConfig) {
            return [rawPreviewConfig, '', ''];
        }
        const { open, onOpenChange, cover, actionsRender, visible, onVisibleChange, rootClassName, maskClassName, mask, forceRender: _forceRender, destroyOnClose: _destroyOnClose, toolbarRender, ...restPreviewConfig } = rawPreviewConfig;
        let onInternalOpenChange;
        if (onOpenChange) {
            onInternalOpenChange = onOpenChange;
        }
        else if (onVisibleChange) {
            onInternalOpenChange = (nextOpen, info) => {
                const { current } = info || {};
                if (current !== undefined) {
                    onVisibleChange(nextOpen, !nextOpen, current);
                }
                else {
                    onVisibleChange(nextOpen, !nextOpen);
                }
            };
        }
        const [coverElement, maskConfig] = normalizeMask(mask);
        return [
            {
                ...restPreviewConfig,
                open: open ?? visible,
                onOpenChange: onInternalOpenChange,
                cover: cover ?? coverElement,
                mask: maskConfig,
                actionsRender: actionsRender ?? toolbarRender,
            },
            rootClassName,
            maskClassName,
        ];
    }, [rawPreviewConfig]);
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('Image');
        if (rawPreviewConfig) {
            [
                ['visible', 'open'],
                ['onVisibleChange', 'onOpenChange'],
                ['maskClassName', 'classNames.cover'],
                ['rootClassName', 'classNames.root'],
                ['toolbarRender', 'actionsRender'],
            ].forEach(([deprecatedName, newName]) => {
                warning.deprecated(!(deprecatedName in rawPreviewConfig), deprecatedName, newName);
            });
            warning(!isValidElement(rawPreviewConfig.mask), 'deprecated', '`mask` used as ReactNode is deprecated. Please use `cover` instead.');
            warning(!('forceRender' in rawPreviewConfig), 'breaking', '`forceRender` is no longer supported.');
            warning(!('destroyOnClose' in rawPreviewConfig), 'breaking', '`destroyOnClose` is no longer supported.');
        }
    }
    return splittedPreviewConfig;
}
